import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Award, Bell, BookOpen, Copy, Gift, LifeBuoy, MapPin, Megaphone, Navigation, Route, ShieldCheck, Star, Wallet } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/AppHeader";
import { DiscountMap, UsedButton, directionsUrl } from "@/components/DiscountMap";
import { api, errMsg, fmtDate, money } from "@/lib/api";

const TABS = [["discounts", "Discounts", Gift], ["promos", "Bonuses", Megaphone], ["trips", "Trips", Route], ["rules", "Rules", ShieldCheck],
  ["training", "Training", BookOpen], ["earnings", "Earnings", Wallet], ["news", "News", Bell], ["help", "Help", LifeBuoy]];
const CAT = { gas: "Gas", car_wash: "Car wash", oil_change: "Oil change", food: "Food", other: "Other" };
const Empty = ({ testId, children }) => <p data-testid={testId} className="lux-card p-6 text-sm text-zinc-500">{children}</p>;

const copy = async (code) => {
  try { await navigator.clipboard.writeText(code); toast.success("Code copied"); } catch { toast.error("Couldn't copy — select the code instead"); }
};

const locate = () => new Promise((res, rej) => (navigator.geolocation
  ? navigator.geolocation.getCurrentPosition((p) => res([p.coords.latitude, p.coords.longitude]), rej, { timeout: 10000, maximumAge: 300000 })
  : rej(new Error("unsupported"))));

const Discounts = () => {
  const [d, setD] = useState(null);
  const [me, setMe] = useState(null);
  const [nearest, setNearest] = useState(false);
  useEffect(() => { locate().then(setMe).catch(() => {}); }, []);
  useEffect(() => {
    const params = { limit: 50, ...(nearest && me ? { near_lat: me[0], near_lng: me[1] } : {}) };
    api.get("/driver/hub/discounts", { params }).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
  }, [nearest, me]);
  const toggleNearest = async () => {
    if (nearest) return setNearest(false);
    if (!me) { try { setMe(await locate()); } catch { return toast.error("Allow location to sort by distance"); } }
    setNearest(true);
  };
  const onLogged = useCallback((id, r) => setD((v) => ({ ...v, items: v.items.map((x) => (x.id === id ? { ...x, my_use_count: r.my_use_count, used_today: true } : x)) })), []);
  if (!d) return null;
  if (!d.items.length) return <Empty testId="hub-discounts-empty">No partner discounts right now.</Empty>;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-zinc-500">Tap a gold pin for the code and directions.</p>
        <button data-testid="hub-discounts-nearest" aria-pressed={nearest} onClick={toggleNearest}
          className={`flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors duration-300 ${nearest ? "border-amber-400 bg-amber-400 text-black" : "border-white/10 text-zinc-300 hover:border-amber-500/40"}`}>
          <Navigation className="h-3.5 w-3.5" />Nearest first</button>
      </div>
      <DiscountMap items={d.items} me={me} onLogged={onLogged} />
      <DiscountCards items={d.items} onLogged={onLogged} />
    </div>
  );
};

const DiscountCards = ({ items, onLogged }) => (
    <div className="grid gap-4 md:grid-cols-2" data-testid="hub-discounts">
      {items.map((x) => (
        <div key={x.id} data-testid={`hub-discount-${x.id}`} className="lux-card space-y-2 p-5">
          <div className="flex items-start justify-between gap-3">
            <div><p className="text-[10px] uppercase tracking-[0.2em] text-amber-400/80">{CAT[x.category]} · {x.partner}</p><h3 className="mt-1 font-serif text-xl text-amber-50">{x.title}</h3></div>
            {x.is_sample && <span className="rounded-full border border-zinc-600 px-2 py-0.5 text-[10px] uppercase text-zinc-400">Sample</span>}
          </div>
          {x.description && <p className="text-sm text-zinc-400">{x.description}</p>}
          {x.code && <div className="flex items-center gap-2"><code data-testid={`hub-discount-code-${x.id}`} className="flex-1 truncate rounded-lg border border-dashed border-amber-400/40 bg-amber-400/5 px-3 py-2 font-mono text-sm text-amber-200">{x.code}</code>
            <button data-testid={`hub-discount-copy-${x.id}`} onClick={() => copy(x.code)} className="btn-gold !px-3 !py-2" aria-label="Copy code"><Copy className="h-4 w-4" /></button></div>}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
            {x.address && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{x.address}</span>}
            {x.distance_miles != null && <span data-testid={`hub-discount-distance-${x.id}`} className="text-amber-300">{x.distance_miles.toFixed(1)} mi</span>}
            {x.lat != null && <a data-testid={`hub-discount-directions-${x.id}`} href={directionsUrl(x)} target="_blank" rel="noopener noreferrer" className="text-amber-300 hover:text-amber-200">Directions</a>}
            {x.link && <a href={x.link} target="_blank" rel="noopener noreferrer" className="text-amber-300 hover:text-amber-200">Partner site</a>}
            {x.expires_on && <span>Expires {x.expires_on}</span>}
          </div>
          <div className="text-xs"><UsedButton x={x} onLogged={onLogged} /></div>
        </div>
      ))}
    </div>
);

const Promos = () => {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/driver/hub/promos").then(({ data }) => setP(data)).catch(() => setP([])); }, []);
  if (!p) return null;
  if (!p.length) return <Empty testId="hub-promos-empty">No bonuses or promotions right now.</Empty>;
  return <div className="grid gap-4 md:grid-cols-2" data-testid="hub-promos">{p.map((x) => (
    <div key={x.id} data-testid={`hub-promo-${x.id}`} className="lux-card p-5">
      <div className="flex items-center justify-between gap-2"><h3 className="font-serif text-xl text-amber-50">{x.title}</h3>
        <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase ${x.status === "running" ? "border-emerald-500/40 text-emerald-300" : "border-zinc-600 text-zinc-400"}`}>{x.status}</span></div>
      <p className="mt-2 text-sm text-zinc-400">{x.details}</p><p className="mt-2 text-xs text-zinc-500">{x.start_date} → {x.end_date}</p>
    </div>))}</div>;
};

const TripRow = ({ t }) => (
  <div data-testid={`hub-trip-${t.trip_id}`} className="flex flex-wrap items-start justify-between gap-3 border-t border-white/5 py-3 first:border-0">
    <div className="min-w-0 flex-1"><p className="text-xs text-zinc-500">{fmtDate(t.start_at || t.date)} · <span className="capitalize">{t.kind}</span>{t.status === "cancelled" ? " · cancelled" : ""}</p>
      <p className="truncate text-sm text-amber-100">{t.pickup}{t.dropoff ? ` → ${t.dropoff}` : ""}</p></div>
    <div className="text-right text-sm"><p className="tabular-nums text-amber-200">{money(t.earnings)}</p>
      {t.rating && <p className="flex items-center justify-end gap-1 text-xs text-zinc-400"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{t.rating}</p>}</div>
  </div>
);

const Trips = () => {
  const [d, setD] = useState(null);
  const [skip, setSkip] = useState(0);
  useEffect(() => { api.get("/driver/hub/trips", { params: { limit: 20, skip } }).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))); }, [skip]);
  if (!d) return null;
  return (
    <div className="space-y-6">
      <div className="lux-card p-4 sm:p-6" data-testid="hub-trips-upcoming"><p className="lux-eyebrow mb-2">Upcoming scheduled trips</p>
        {d.upcoming.length ? d.upcoming.map((t) => <TripRow key={t.trip_id} t={t} />) : <p className="text-sm text-zinc-500">No scheduled trips assigned to you.</p>}</div>
      <div className="lux-card p-4 sm:p-6" data-testid="hub-trips-past"><p className="lux-eyebrow mb-2">Past trips</p>
        {d.past.length ? d.past.map((t) => <TripRow key={t.trip_id} t={t} />) : <p className="text-sm text-zinc-500">No past trips yet.</p>}
        {d.past_total > 20 && <div className="mt-3 flex items-center gap-3 text-xs text-zinc-400">
          <button data-testid="hub-trips-newer" disabled={!skip} onClick={() => setSkip(Math.max(0, skip - 20))} className="text-amber-300 disabled:opacity-40">Newer</button>
          <span>{skip + 1}–{Math.min(skip + 20, d.past_total)} of {d.past_total}</span>
          <button data-testid="hub-trips-older" disabled={skip + 20 >= d.past_total} onClick={() => setSkip(skip + 20)} className="text-amber-300 disabled:opacity-40">Older</button></div>}
      </div>
    </div>
  );
};

const Rules = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/driver/hub/rules").then(({ data }) => setD(data)); }, []);
  if (!d) return null;
  return <div className="grid gap-4 md:grid-cols-2" data-testid="hub-rules">{d.sections.map((s) => (
    <div key={s.key} data-testid={`hub-rule-${s.key}`} className="lux-card p-5"><h3 className="font-serif text-lg text-amber-50">{s.title}</h3>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-zinc-400">{s.items.map((i) => <li key={i}>{i}</li>)}</ul></div>))}</div>;
};

const Training = ({ o }) => {
  const t = o.training;
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="hub-training">
      <div className="lux-card p-5"><p className="lux-eyebrow">Training status</p>
        <p data-testid="hub-training-status" className="mt-2 font-serif text-2xl text-amber-50">{t.passed ? "Passed" : "Not passed yet"}</p>
        <p className="mt-1 text-sm text-zinc-400">Score {t.score ?? "—"}% · pass mark {t.pass_score}% · {t.modules_watched}/{t.modules_total} modules watched</p>
        <Link to="/driver/training" data-testid="hub-training-link" className="btn-outline-gold mt-4 inline-flex">Open training</Link></div>
      {t.certificate ? (
        <div data-testid="hub-certificate" className="lux-card border-amber-400/40 p-6 text-center">
          <Award className="mx-auto h-10 w-10 text-amber-400" />
          <p className="lux-eyebrow mt-3">Certificate of completion</p>
          <p className="mt-2 font-serif text-2xl text-amber-50">{t.certificate.name}</p>
          <p className="mt-1 text-sm text-zinc-400">completed Luxury Rideshare driver training with {t.certificate.score}%{t.certificate.passed_at ? ` on ${fmtDate(t.certificate.passed_at)}` : ""}</p>
          {t.certificate.driver_code && <p className="mt-1 text-xs text-zinc-500">Driver {t.certificate.driver_code}</p>}
          <button data-testid="hub-certificate-print" onClick={() => window.print()} className="btn-gold mt-4">Print certificate</button>
        </div>
      ) : <Empty testId="hub-certificate-none">Pass the training quiz to get your certificate.</Empty>}
    </div>
  );
};

const Earnings = ({ o }) => (
  <div className="lux-card p-6" data-testid="hub-earnings">
    <p className="lux-eyebrow">Earnings</p>
    <p className="gold-text mt-2 font-serif text-4xl font-semibold">{money(o.earnings.today)}</p><p className="text-xs text-zinc-500">today</p>
    <p className="mt-2 text-sm text-zinc-400">This week {money(o.earnings.this_week)} · All time {money(o.earnings.all_time)}</p>
    <Link to="/driver/earnings" data-testid="hub-earnings-link" className="btn-gold mt-4 inline-flex">Earnings & withdrawals</Link>
  </div>
);

const News = ({ onRead }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/driver/hub/news", { params: { limit: 30 } }).then(({ data }) => setD(data)), []);
  useEffect(() => { load(); }, [load]);
  const read = async (a) => { if (!a.unread) return; await api.post(`/driver/hub/news/${a.id}/read`).catch(() => {}); load(); onRead(); };
  if (!d) return null;
  if (!d.items.length) return <Empty testId="hub-news-empty">No announcements yet.</Empty>;
  return <div className="space-y-3" data-testid="hub-news">{d.items.map((a) => (
    <details key={a.id} data-testid={`hub-news-${a.id}`} onToggle={(e) => e.currentTarget.open && read(a)} className="lux-card p-5">
      <summary className="flex cursor-pointer items-start gap-3">
        {a.unread && <span data-testid={`hub-news-unread-${a.id}`} className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-amber-400" aria-label="Unread" />}
        <div className="min-w-0 flex-1"><p className="font-medium text-amber-50">{a.pinned && <span className="mr-2 text-[10px] uppercase tracking-widest text-amber-400">Pinned</span>}{a.title}</p>
          <p className="text-xs text-zinc-500">{fmtDate(a.publish_at)}</p></div>
      </summary>
      <p className="mt-3 whitespace-pre-line text-sm text-zinc-300">{a.body}</p>
    </details>))}</div>;
};

const Help = ({ o }) => (
  <div className="grid gap-4 md:grid-cols-2" data-testid="hub-help">
    <div className="lux-card space-y-2 p-5"><p className="lux-eyebrow">Contact us</p>
      <a href={`tel:${o.help.phone}`} data-testid="hub-help-phone" className="block font-serif text-2xl text-amber-50">{o.help.phone}</a>
      <a href={`mailto:${o.help.email}`} data-testid="hub-help-email" className="block text-sm text-amber-300">{o.help.email}</a></div>
    <div className="lux-card space-y-3 p-5"><p className="lux-eyebrow">Get help</p>
      <Link to="/support" data-testid="hub-help-ticket" className="btn-gold inline-flex">Open a support ticket</Link>
      <p className="text-xs text-zinc-500">Or tap the gold assistant button at the bottom of the screen to ask our AI assistant.</p></div>
  </div>
);

export default function DriverHub() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "discounts";
  const [o, setO] = useState(null);
  const load = useCallback(() => api.get("/driver/hub").then(({ data }) => setO(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const Body = { discounts: Discounts, promos: Promos, trips: Trips, rules: Rules }[tab];
  return (
    <PortalShell>
      <div data-testid="driver-hub-page" className="space-y-6">
        <div><p className="lux-eyebrow">Drivers only</p><h1 className="mt-2 text-4xl font-semibold text-amber-50 sm:text-5xl">Driver Hub</h1></div>
        <div className="-mx-1 flex gap-2 overflow-x-auto pb-1" role="tablist" data-testid="hub-tabs">
          {TABS.map(([k, l, Icon]) => (
            <button key={k} role="tab" aria-selected={tab === k} data-testid={`hub-tab-${k}`} onClick={() => setParams({ tab: k })}
              className={`relative flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${tab === k ? "border-amber-400 bg-amber-400 text-black" : "border-white/10 text-zinc-300 hover:border-amber-500/40"}`}>
              <Icon className="h-4 w-4" />{l}
              {k === "news" && o?.news_unread > 0 && <span data-testid="hub-news-unread-badge" className="ml-0.5 rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">{o.news_unread}</span>}
            </button>
          ))}
        </div>
        {Body && <Body />}
        {o && tab === "training" && <Training o={o} />}
        {o && tab === "earnings" && <Earnings o={o} />}
        {tab === "news" && <News onRead={load} />}
        {o && tab === "help" && <Help o={o} />}
      </div>
    </PortalShell>
  );
}
